import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAttributesStyles } from "@insite/content-library/Components/ProductAttributes";
import { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import PlusCircle from "@insite/mobius/Icons/PlusCircle";
import { LinkProps } from "@insite/mobius/Link";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { getProp } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { BlockProps } from "@store/Components/Common/Block";
import { BlockVariantSide } from "@store/Components/Common/Block/variant/base";
import { BlockVariant } from "@store/Components/Common/Block/variant/config";
import { SMALLEST_HEADER_HEIGHT } from "@store/Overrides/Widgets/Header/LogoLinksSearch";
import { contentSpacingStyles, editLinkStyles, gridGapStyles } from "@store/theme/common/mixins";
import { tableGridProps } from "@store/theme/common/props/tableGrid";
import { TYPOGRAPHY } from "@store/theme/design";
import { mediaOptions } from "@store/utilities/breakpointMediaQueries";
import breakpointValuesMediaQueries from "@store/utilities/breakpointValuesMediaQueries";
import isWebsiteAccessedByMobileApp from "@store/utilities/isWebsiteAccessedByMobileApp";
import { css } from "styled-components";

export interface StoreProductDetailsViewStyles {
    headingBlock?: BlockProps;
    headingNumbers?: InjectableCss;
    detailBlock?: BlockProps;
    detailActions?: InjectableCss;
    detailActionsInnerWrapper?: InjectableCss;
    detailQtyInnerWrapper?: InjectableCss;
    detailActionAndMessageWrapper?: InjectableCss;
    detailActionWrapper?: InjectableCss;
    detailGrid?: GridContainerProps;
    detailThumbnailsGridItem?: GridItemProps;
    detailContentGridItem?: GridItemProps;
    detailContentDescription?: TypographyPresentationProps;
    detailContentMoreLink?: LinkProps;
    detailImageGridItem?: GridItemProps;
    detailImageWrapper?: InjectableCss;
    detailImage?: InjectableCss;
    detailPriceWrapper?: InjectableCss;
    configurationBlock?: BlockProps;
    anchorBlock?: BlockProps;
    infoBlock?: BlockProps;
    infoGrid?: GridContainerProps;
    infoDescriptionGridItem?: GridItemProps;
    infoDocumentsGridItem?: GridItemProps;
    branchAvailability?: BlockProps;
    attributesBlock?: BlockProps;
    schematicsBlock?: BlockProps;
    tabBlock?: BlockProps;
    relatedBlock?: BlockProps;
    sectionTitle?: TypographyPresentationProps;
    addToListLink?: ProductAddToListLinkStyles;
    highDemandModeText?: TypographyPresentationProps;
    attributeBlock: InjectableCss;

    prodInfosGrid?: InjectableCss;
    prodInfosGridItem?: InjectableCss;
    prodInfosGridItemRight?: InjectableCss;
    prodInfosGridItemRightBottom?: InjectableCss;
    container?: InjectableCss;
    popoutHeadingBlock?: BlockProps;
    hiddenPopoutHeadingBlock?: BlockProps;
    tableGridHead?: BlockProps;
    headingNumbersGridHeader?: InjectableCss;
    detailQtyInnerWrapperGridHeader?: InjectableCss;
    detailActionWrapperGridHeader?: InjectableCss;
    wrapper?: InjectableCss;
}

const baseBlockCss = css`
    position: relative;
    z-index: 1;
`;

const separatedBlockProps: BlockProps = {
    variant: BlockVariant.Separated,
    variantSide: BlockVariantSide.Top,
    containerCss: baseBlockCss,
};

const anchorMenuGap = isWebsiteAccessedByMobileApp() ? 0 : SMALLEST_HEADER_HEIGHT;

export default {
    prodInfosGrid: {
        css: css`
            display: grid;
            column-gap: 40px;
            row-gap: 1em;
            align-items: center;
        `,
    },
    prodInfosGridItem: {
        css: css`
            grid-column-start: 1;
            grid-column-end: 1;
        `,
    },
    prodInfosGridItemRight: {
        css: css`
            grid-column-start: 2;
            grid-column-end: 2;

            & > button {
                display: flex;
            }
        `,
    },
    prodInfosGridItemRightBottom: {
        css: css`
            grid-column-start: 2;
            grid-column-end: 2;
            align-self: end;
        `,
    },
    popoutHeadingBlock: {
        containerCss: css`
            position: fixed;
            z-index: 2;
            transition: all 0.5s ease-in-out;
            transform: translateY(60px);
            visibility: visible;
            height: auto;
            top: ${(anchorMenuGap / 2) - 5}px;
            width: 100%;
            background: ${getColor("secondary.contrast")};
            box-shadow: ${getProp("theme.shadows.small")};
            border-top: 1px solid ${getColor("common.border")};

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                    { values: [null, null, null, "fixed"], properties: ["position"] },
                    { values: [null, null, null, "unset"], properties: ["top"] },
                    { values: [null, null, null, "10px"], properties: ["bottom"] },
                    ],

                    mediaOptions.max,
                )}
        `,
        spacerCss: css`
            margin: 0 auto !important;
            position: relative;
            top: 40px;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                    { values: [null, null, null, "20px"], properties: ["top"] },
                    ],

                    mediaOptions.max,
                )}
        `,
        wrapperCss: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                    { values: [null, null, null, "relative"], properties: ["position"] },
                    { values: [null, null, null, "-20px"], properties: ["top"] },
                    ],

                    mediaOptions.max,
                )}
        `
    },
    container: tableGridProps.container,
    hiddenPopoutHeadingBlock: {
        containerCss: css`
            transform: translateY(0);
            visibility: hidden;
            height:0;
        `
    },
    tableGridHead: {
        ...tableGridProps.tableGridHead,
        wrapperCss: css`
            ${tableGridProps.tableGridHead?.css}
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            padding: 1em 0;
        `
    },
    headingNumbersGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [0, 0, 0, 6, 6],
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
            background: ${getColor("secondary.contrast")};

            [data-test-selector="productDetails_productDescription"] {
                font-size: ${TYPOGRAPHY.size.larger};
            }
        `
    },
    detailQtyInnerWrapperGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [7, 6, 6, 2, 2],
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            display: flex;
            justify-content: center;
            align-self: center;
            background: ${getColor("secondary.contrast")};

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                    { values: [null, null, null, "row"], properties: ["flex-direction"] },
                    { values: [null, null, null, "wrap"], properties: ["flex-wrap"] },
                    ],
                    mediaOptions.max,
                )}
        `
    },
    detailActionWrapperGridHeader: {
        ...tableGridProps.tableGridHeader,
        width: [5, 6, 6, 4, 4],
        css: css`
            ${tableGridProps.tableGridHeader?.css}
            display: flex;
            align-self: center;
            background: ${getColor("secondary.contrast")};
        `
    },
    headingBlock: {
        wrapperCss: css`
            padding: 0 0 1em;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    headingNumbers: {
        css: css`
            display: flex;
            flex-flow: wrap;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        {
                            values: [TYPOGRAPHY.size.smallest, null, null, null, TYPOGRAPHY.size.smaller],
                            properties: ["font-size"],
                        },
                    ],
                    mediaOptions.min,
                )}

            > * {
                &::after {
                    content: "";
                    position: relative;
                    top: 0.15em;
                    display: inline-block;
                    width: 1px;
                    height: 1.1em;
                    margin: 0 0.7em;
                    background-color: ${getColor("common.borderDark")};
                }

                &:last-child::after {
                    display: none;
                }
            }
        `,
    },
    detailActions: {
        css: css`
            ${contentSpacingStyles("0.5em", null)}
            width:100%;
        `,
    },
    detailActionsInnerWrapper: {
        css: css`
            display: flex;
            flex-flow: wrap;
            width: 100%;
        `,
    },
    detailQtyInnerWrapper: {
        css: css`
            display: flex;
            flex-flow: wrap;
            justify-content: space-between;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: ["column", "row"], properties: ["flex-direction"] },
                    ],
                    mediaOptions.min,
                )}

            & > div {
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [
                            { values: ["2px", 0], properties: ["margin-top", "margin-bottom"] },
                        ],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    detailActionWrapper: {
        css: css`
            display: flex;
            flex-flow: wrap;
            margin-top: 1em;
            width: 100%;
        `,
    },
    detailActionAndMessageWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: -0.5em;
            margin-left: 0;
        `,
    },
    detailBlock: {
        spacerCss: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, 75, null, 100], properties: ["margin-bottom"], unit: "px" }],
                    mediaOptions.min,
                )}
        `,
    },
    detailGrid: {
        gap: 30,
    },
    detailThumbnailsGridItem: {
        width: [12, 12, 1, 1, 1],
    },
    detailContentGridItem: {
        width: [12, 12, 6, 5, 5],
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, 20, 30, 60], properties: ["padding-left"], unit: "px" }],
                    mediaOptions.min,
                )}
        `,
    },
    addToListLink: {
        link: {
            css: css`
                ${editLinkStyles()}
            `,
            icon: {
                iconProps: {
                    src: PlusCircle,
                    css: css`
                        height: 1.5em;
                        width: 1.5em;
                        svg {
                            height: inherit;
                            width: inherit;
                        }
                    `,
                },
            },
            typographyProps: {
                css: css`
                    padding-left: 5px;
                `,
            },
        },
    },
    detailContentDescription: {
        forwardAs: "p",
        css: css`
            ${contentSpacingStyles(TYPOGRAPHY.spacing.base, null)}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, "none"], properties: ["display"] }],
                    mediaOptions.max,
                )}
        `,
    },
    detailContentMoreLink: {
        css: css<any>`
            ${({ theme }) => theme?.link?.defaultProps?.css}
            font-size: ${TYPOGRAPHY.size.smaller};
            font-weight: ${TYPOGRAPHY.weight.black};
            ${contentSpacingStyles(TYPOGRAPHY.spacing.base, null)}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, "none"], properties: ["display"] }],
                    mediaOptions.max,
                )}
        `,
    },
    detailImageGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [
                        { values: [null, null, 20, 30, 60], properties: ["padding-right"], unit: "px" },
                        { values: ["center", null, null, null, "flex-start"], properties: ["justify-content"] },
                    ],
                    mediaOptions.min,
                )}
        `,
    },
    detailImageWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            max-width: 630px;
        `,
    },
    detailPriceWrapper: {
        css: css`
            display: flex;
            align-items: flex-end;
            justify-content: space-between;
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [100, null, 100, 75, 50], properties: ["width"], unit: "%" }],
                    mediaOptions.min,
                )}
        `,
    },
    detailImage: {},
    anchorBlock: {
        containerCss: css`
            position: sticky;
            background-color: ${getColor("common.accent")};
            box-shadow: ${getProp("theme.shadows.small")};
            z-index: 3;

            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, anchorMenuGap, null, null], properties: ["top"], unit: "px" }],
                    mediaOptions.min,
                )}
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, "none"], properties: ["display"] }],
                    mediaOptions.max,
                )}
        `,
        spacerCss: css`
            ${props =>
                breakpointValuesMediaQueries(
                    props.theme,
                    [{ values: [null, null, 60, null, 75], properties: ["margin-top", "margin-bottom"], unit: "px" }],
                    mediaOptions.min,
                )}
        `,
    },
    infoBlock: {
        containerCss: baseBlockCss,
    },
    infoGrid: {
        gap: 0,
        css: css`
            ${gridGapStyles(["30px"], [TYPOGRAPHY.spacing.base])}
        `,
    },
    infoDescriptionGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;

            div {
                ${props =>
                    breakpointValuesMediaQueries(
                        props.theme,
                        [{ values: [null, null, 520], properties: ["max-width"], unit: "px" }],
                        mediaOptions.min,
                    )}
            }
        `,
    },
    infoDocumentsGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    configurationBlock: separatedBlockProps,
    branchAvailability: separatedBlockProps,
    attributesBlock: separatedBlockProps,
    schematicsBlock: separatedBlockProps,
    tabBlock: separatedBlockProps,
    relatedBlock: {
        variant: BlockVariant.Bordered,
        variantSide: BlockVariantSide.Top,
        containerCss: baseBlockCss,
    },
    sectionTitle: {
        variant: "h5",
        forwardAs: "h3",
        css: css`
            margin-bottom: 1.25em;
            position: relative;
        `,
    },
    highDemandModeText: {
        color: "text.accent",
        italic: true,
        size: TYPOGRAPHY.size.smallest,
        weight: TYPOGRAPHY.weight.bold,
    },
    attributeBlock: {
        css: css`
            ${baseBlockCss}
            @media print {
                position: relative !important;
                display: block !important;
                & > div {
                    position: relative !important;
                    display: block !important;
                    page-break-inside: avoid;
                    padding-bottom: 30px;
                    margin-bottom: 0px;
                    margin-top: 0px;
                }
            }
        `,
    },
} as StoreProductDetailsViewStyles;
